/**
 * Context menu utilities for handling right-clicks on links
 * and providing customized context menus.
 */

/**
 * Creates a tooltip element to indicate success
 * @param e - The original mouse event
 * @param messageString - The message to display in the tooltip
 */
export const createTooltip = (e: MouseEvent, messageString: string): void => {
    const tooltip = document.createElement('div');
    tooltip.textContent = messageString;
    tooltip.style.position = 'fixed';
    tooltip.style.top = `${e.clientY - 30}px`;
    tooltip.style.left = `${e.clientX}px`;
    tooltip.style.background = 'rgba(0,0,0,0.7)';
    tooltip.style.color = 'white';
    tooltip.style.padding = '5px 10px';
    tooltip.style.borderRadius = '3px';
    tooltip.style.fontSize = '12px';
    tooltip.style.zIndex = '10000';

    document.body.appendChild(tooltip);
    setTimeout(() => {
        document.body.removeChild(tooltip);
    }, 1500);
};

/**
 * Interface for menu item configuration
 */
interface MenuItem {
    text: string;
    action: () => void;
}

/**
 * Creates and manages a custom context menu for link actions
 * @param e - The original mouse event
 * @param link - The HTML anchor element that was right-clicked
 * @param url - The URL of the link
 */
export const createCustomContextMenu = (e: MouseEvent, link: HTMLAnchorElement, url: string): void => {
    // Create the menu container
    const menu = document.createElement('div');
    menu.className = 'custom-context-menu';
    menu.style.position = 'fixed';
    menu.style.top = `${e.clientY}px`;
    menu.style.left = `${e.clientX}px`;
    menu.style.background = 'white';
    menu.style.border = '1px solid #ccc';
    menu.style.borderRadius = '2px';
    menu.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';
    menu.style.zIndex = '9999';
    menu.style.width = '200px';

    // Define menu items with their actions
    const menuItems: MenuItem[] = [
        {
            text: 'Open',
            action: () => {
                window.location.href = url;
            },
        },
        {
            text: 'Open in new tab',
            action: () => {
                window.parent.postMessage(
                    {
                        action: 'openInNewTab',
                        url,
                        originalTarget: '_blank',
                    },
                    '*',
                );
                console.log('Context menu handler intercepted, sending to parent:', url);
            },
        },
        {
            text: 'Copy link address',
            action: () => {
                navigator.clipboard.writeText(url).then(() => {
                    createTooltip(e, 'Link copied!');
                });
            },
        },
        {
            text: 'Copy text',
            action: () => {
                const linkText = link.textContent || '';
                navigator.clipboard.writeText(linkText).then(() => {
                    createTooltip(e, 'Text copied!');
                });
            },
        },
    ];

    // Build the menu items
    menuItems.forEach((item, index) => {
        const menuItem = document.createElement('div');
        menuItem.innerText = item.text;
        menuItem.style.padding = '4px 12px';
        menuItem.style.cursor = 'pointer';
        menuItem.style.fontSize = '14px';
        menuItem.style.whiteSpace = 'nowrap';

        menuItem.addEventListener('mouseenter', () => {
            menuItem.style.background = '#f0f0f0';
        });

        menuItem.addEventListener('mouseleave', () => {
            menuItem.style.background = 'transparent';
        });

        menuItem.addEventListener('click', () => {
            item.action();
            document.body.removeChild(menu);
        });

        menu.appendChild(menuItem);

        // Add a grey divider after the first 2 options
        if (index === 1) {
            const divider = document.createElement('div');
            divider.style.height = '1px';
            divider.style.background = '#e0e0e0';
            divider.style.margin = '0';
            menu.appendChild(divider);
        }
    });

    // Add to document
    document.body.appendChild(menu);

    // Close menu when clicking elsewhere
    const closeMenu = (event: MouseEvent) => {
        if (!menu.contains(event.target as Node)) {
            if (document.body.contains(menu)) {
                document.body.removeChild(menu);
            }
            document.removeEventListener('mousedown', closeMenu);
        }
    };

    // Add a small delay before adding the click listener
    setTimeout(() => {
        document.addEventListener('mousedown', closeMenu);
    }, 10);

    // Also close the menu if user scrolls or resizes the window
    const closeMenuOnScroll = () => {
        if (document.body.contains(menu)) {
            document.body.removeChild(menu);
        }
        window.removeEventListener('scroll', closeMenuOnScroll);
        window.removeEventListener('resize', closeMenuOnScroll);
    };

    window.addEventListener('scroll', closeMenuOnScroll);
    window.addEventListener('resize', closeMenuOnScroll);
};

/**
 * Handler for link clicks that should open in a new tab
 * @param e - The original mouse event
 */
export const handleLinkClick = (e: MouseEvent): void => {
    const link = e.currentTarget as HTMLAnchorElement;
    if (e.ctrlKey || e.metaKey || e.button === 1 || link.target === '_blank') {
        e.preventDefault();
        e.stopPropagation();

        const url = link.href;
        if (url && url !== '#') {
            window.parent.postMessage(
                {
                    action: 'openInNewTab',
                    url,
                    originalTarget: link.target,
                },
                '*',
            );
            console.log('Direct link handler intercepted, sending to parent:', url);
        }
    }
};

/**
 * Handler for right-clicks on links to show custom context menu
 * @param e - The original mouse event
 */
export const handleContextMenu = (e: MouseEvent): void => {
    // Prevent the default context menu
    e.preventDefault();
    e.stopPropagation();

    // Only intercept context menu on links
    const link = (e.target as Element).closest('a');
    if (!link) return;

    const url = link.href;
    if (!url || url === '#') return;

    createCustomContextMenu(e, link as HTMLAnchorElement, url);
};

/**
 * Adds link handlers to all links in the document
 */
export const addLinkHandlers = (): void => {
    const links = document.querySelectorAll<HTMLAnchorElement>('a');
    links.forEach((link) => {
        // Skip if we've already processed this link
        if (link.dataset.linkInterceptorAttached) return;

        // Mark as processed
        link.setAttribute('data-link-interceptor-attached', 'true');

        // Add click handler
        link.addEventListener('click', handleLinkClick as EventListener);

        // Add context menu handler
        link.addEventListener('contextmenu', handleContextMenu as EventListener);
    });
};

/**
 * Sets up link handler observers for dynamically added links
 * @returns Cleanup function to remove observers
 */
export const setupLinkHandlerObservers = (): (() => void) => {
    // Run initially
    addLinkHandlers();

    // Add listener for postMessage acknowledgment
    const messageListener = (event: MessageEvent) => {
        if (event.data && event.data.action === 'openInNewTabAcknowledged') {
            console.log('Parent window acknowledged open in new tab request');
        }
    };

    window.addEventListener('message', messageListener);

    // Set up MutationObserver to watch for new links with performance optimization
    const observer = new MutationObserver((mutations) => {
        // Only process mutations that could have added links
        let shouldProcess = false;

        Array.from(mutations).forEach((mutation) => {
            if (mutation.addedNodes.length > 0 || mutation.type === 'childList') {
                shouldProcess = true;
            }
        });

        if (shouldProcess) {
            // Debounce the handler to avoid excessive processing
            if (window._linkHandlerTimeout) {
                clearTimeout(window._linkHandlerTimeout);
            }

            const timeoutId = setTimeout(() => {
                addLinkHandlers();
                delete window._linkHandlerTimeout;
            }, 100);
            window._linkHandlerTimeout = timeoutId;
        }
    });

    // Start observing with more specific parameters
    observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: false,
        characterData: false,
    });

    // Return cleanup function
    return () => {
        observer.disconnect();

        // Clean up timeout if it exists
        if (window._linkHandlerTimeout) {
            clearTimeout(window._linkHandlerTimeout);
            delete window._linkHandlerTimeout;
        }

        // Remove message event listener
        window.removeEventListener('message', messageListener);

        // Clean up individual link handlers
        const links = document.querySelectorAll<HTMLAnchorElement>('a[data-link-interceptor-attached]');
        links.forEach((link) => {
            link.removeEventListener('click', handleLinkClick as EventListener);
            link.removeEventListener('contextmenu', handleContextMenu as EventListener);
            // Remove the data attribute
            link.removeAttribute('data-link-interceptor-attached');
        });
    };
};
